import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useOnboarding } from "./OnboardingProvider";
import { ISpotlightRect, useSpotlight } from "./hooks/useSpotlight";
import { IOnboardingStep, IOnboardingSubstep } from "./flows/onboardingTypes";
import { useAppContext } from "../../AppContext";
import styles from "./styles/onboarding.module.scss";

import { dvSize } from "../../common/infrastructure/utilities/viewportUnitsUtility";
/** When in substep mode, use the substep's optional flag if set; otherwise fall back to the step's optional flag. */
function isCurrentOptional(step: IOnboardingStep, substep: IOnboardingSubstep | null): boolean {
  if (substep && substep.optional !== undefined) return substep.optional;
  return step.optional ?? false;
}

const ARROW_SIZE = 24;
const ARROW_GAP = 6;
const TOOLTIP_GAP = ARROW_GAP + ARROW_SIZE + 6;

const ease = [0.32, 0.72, 0, 1];

export const OnboardingOverlay = () => {
  const onboarding = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, selectedStudio } = useAppContext();

  const activeStep = useMemo(() => {
    if (!onboarding?.activeStepId) return null;
    return onboarding.effectiveSteps.find(
      (s) => s.id === onboarding.activeStepId
    );
  }, [onboarding?.activeStepId, onboarding?.effectiveSteps]);

  // Determine what to show: substep (if any) or the step itself
  const currentSubstep = onboarding?.currentSubstep ?? null;
  const hasSubsteps = !!activeStep?.substeps && activeStep.substeps.length > 0;

  const displayTitle = hasSubsteps && currentSubstep
    ? currentSubstep.title
    : activeStep?.title ?? "";

  const rawDescription = hasSubsteps && currentSubstep
    ? currentSubstep.description
    : activeStep?.description ?? "";

  // Substitute dynamic placeholders — currently only {studioEmail}.
  // Keeps step definitions as plain strings while allowing a few per-user values.
  const displayDescription = rawDescription.replace(
    "{studioEmail}",
    selectedStudio?.email ?? "",
  );

  const displaySelector = hasSubsteps && currentSubstep
    ? currentSubstep.targetSelector ?? null
    : activeStep?.targetSelector ?? null;

  const displayPlacement = hasSubsteps && currentSubstep
    ? currentSubstep.placement
    : activeStep?.placement;

  // Navigate to step's route only when a new step activates.
  // Must NOT re-run on location changes — otherwise the user gets trapped
  // in a redirect loop when navigating away (e.g. clicking + to add a plan).
  useEffect(() => {
    if (activeStep?.route && location.pathname !== activeStep.route) {
      navigate(activeStep.route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep?.id]);

  const { rect, recalculate, isScrolling } = useSpotlight(displaySelector);

  // Recalculate after route navigation or substep change settles
  useEffect(() => {
    if (activeStep) {
      const timer = setTimeout(recalculate, 300);
      return () => clearTimeout(timer);
    }
  }, [activeStep, recalculate, onboarding?.activeSubstepIndex]);

  // Retry spotlight when element not yet in DOM (e.g. waiting for data to load)
  useEffect(() => {
    if (!displaySelector || rect) return;
    const timer = setTimeout(recalculate, 600);
    return () => clearTimeout(timer);
  }, [displaySelector, rect, recalculate]);

  const handleGotIt = useCallback(() => {
    if (!onboarding || !activeStep) return;

    if (hasSubsteps) {
      onboarding.advanceSubstep();
    } else {
      onboarding.completeStep(activeStep.id);
    }
  }, [onboarding, activeStep, hasSubsteps]);

  const handleSkip = useCallback(() => {
    if (!onboarding || !activeStep) return;
    onboarding.completeStep(activeStep.id);
  }, [onboarding, activeStep]);

  const handleDismiss = useCallback(() => {
    if (!onboarding) return;
    onboarding.dismissCurrentStep();
  }, [onboarding]);

  // Wait for the target element before showing the overlay to prevent
  // flicker (tooltip appearing centered then jumping to position).
  // Falls back after 1s for elements that may not exist (e.g. sidebar on mobile).
  const isTargetResolved = !displaySelector || !!rect;
  const [fallbackReady, setFallbackReady] = useState(false);

  useEffect(() => {
    setFallbackReady(false);
    if (isTargetResolved) return;
    const timer = setTimeout(() => setFallbackReady(true), 1000);
    return () => clearTimeout(timer);
  }, [activeStep?.id, onboarding?.activeSubstepIndex, isTargetResolved]);

  const isVisible =
    !!onboarding?.activeStepId && !!activeStep && !isScrolling && (isTargetResolved || fallbackReady);

  const effectivePlacement = isVisible
    ? resolveEffectivePlacement(rect, displayPlacement, isMobile)
    : displayPlacement;

  const tooltipStyle = isVisible
    ? computeTooltipPosition(rect, effectivePlacement, isMobile)
    : {};

  const arrowInfo = isVisible ? getArrowInfo(rect, effectivePlacement) : null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="overlay"
          className={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease }}
        >
          <svg className={styles.overlayMask}>
            <defs>
              <linearGradient
                id="onboarding-gradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="rgba(196, 63, 89, 0.55)" />
                <stop offset="50%" stopColor="rgba(108, 99, 255, 0.45)" />
                <stop offset="100%" stopColor="rgba(196, 63, 89, 0.55)" />
              </linearGradient>
              {rect && (
                <mask id="onboarding-spotlight-mask">
                  <rect width="100%" height="100%" fill="white" />
                  <rect
                    x={rect.left}
                    y={rect.top}
                    width={rect.width}
                    height={rect.height}
                    rx="8"
                    ry="8"
                    fill="black"
                  />
                </mask>
              )}
            </defs>
            <rect
              className={styles.overlayGradient}
              fill="url(#onboarding-gradient)"
              mask={rect ? "url(#onboarding-spotlight-mask)" : undefined}
            />
          </svg>

          {/* Animated arrow pointing at target */}
          {rect && arrowInfo && (
            <div
              className={`${styles.spotlightArrow} ${arrowInfo.bounceClass}`}
              style={arrowInfo.position}
            >
              <svg
                width={ARROW_SIZE}
                height={ARROW_SIZE}
                viewBox="0 0 24 24"
                fill="none"
                style={{ transform: `rotate(${arrowInfo.rotation}deg)` }}
              >
                <path
                  d="M12 5L12 19M7 14L12 19L17 14"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}

          <motion.div
            key={displaySelector ?? "tooltip"}
            className={styles.tooltip}
            style={tooltipStyle}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease }}
          >
            <h4 className={styles.tooltipTitle}>{displayTitle}</h4>
            <p className={styles.tooltipDescription}>{displayDescription}</p>
            <div className={styles.tooltipActions}>
              <button className={styles.tooltipPrimaryBtn} onClick={handleGotIt}>
                Добре
              </button>
              {isCurrentOptional(activeStep!, currentSubstep) ? (
                <button
                  className={styles.tooltipSecondaryBtn}
                  onClick={handleSkip}
                >
                  Пропусни засега
                </button>
              ) : (
                <button
                  className={styles.tooltipSecondaryBtn}
                  onClick={handleDismiss}
                >
                  По-късно
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Resolve the placement that will actually fit on screen. Both
 * computeTooltipPosition and getArrowInfo consume this so the dialog and arrow
 * never disagree.
 *
 * - Mobile: always collapse to top/bottom (no horizontal room for a side dialog).
 * - Desktop/tablet: if the requested left/right placement would push the
 *   tooltip past the viewport edge, flip to top/bottom (whichever side has
 *   more room).
 */
function resolveEffectivePlacement(
  rect: ISpotlightRect | null,
  placement: string | undefined,
  isMobile: boolean,
): string | undefined {
  if (!rect) return placement;

  const gap = TOOLTIP_GAP;
  const belowSpace = window.innerHeight - (rect.top + rect.height + gap);
  const aboveSpace = rect.top - gap;
  const pickVertical = () => (belowSpace >= aboveSpace ? "bottom" : "top");

  if (isMobile) {
    const ESTIMATED_TOOLTIP_HEIGHT = 260;
    const placeAbove =
      placement === "top" ||
      (belowSpace < ESTIMATED_TOOLTIP_HEIGHT && aboveSpace > belowSpace);
    return placeAbove ? "top" : "bottom";
  }

  // Only fall back when there's basically no room at all on the requested
  // side. When there's some room, computeTooltipPosition clamps the tooltip
  // into view (mild overlap with the spotlight is acceptable; going off-screen
  // is not).
  const minHorizontal = 80;

  if (placement === "left" && rect.left < minHorizontal) return pickVertical();
  if (
    placement === "right" &&
    window.innerWidth - (rect.left + rect.width) < minHorizontal
  ) {
    return pickVertical();
  }

  return placement;
}

function getArrowInfo(
  rect: ISpotlightRect | null,
  placement: string | undefined
): {
  position: React.CSSProperties;
  rotation: number;
  bounceClass: string;
} | null {
  if (!rect) return null;

  // Arrow sits between spotlight and tooltip, pointing AT the target.
  // Base SVG points DOWN (↓). Rotations (clockwise): 90→LEFT, 180→UP, 270→RIGHT.
  switch (placement) {
    case "bottom":
      return {
        position: {
          top: rect.top + rect.height + ARROW_GAP,
          left: rect.left + rect.width / 2 - ARROW_SIZE / 2,
        },
        rotation: 180,
        bounceClass: styles.bounceUp,
      };
    case "top":
      return {
        position: {
          top: rect.top - ARROW_SIZE - ARROW_GAP,
          left: rect.left + rect.width / 2 - ARROW_SIZE / 2,
        },
        rotation: 0,
        bounceClass: styles.bounceDown,
      };
    case "left":
      return {
        position: {
          top: rect.top + rect.height / 2 - ARROW_SIZE / 2,
          left: rect.left - ARROW_SIZE - ARROW_GAP,
        },
        rotation: 270,
        bounceClass: styles.bounceRight,
      };
    case "right":
    default:
      return {
        position: {
          top: rect.top + rect.height / 2 - ARROW_SIZE / 2,
          left: rect.left + rect.width + ARROW_GAP,
        },
        rotation: 90,
        bounceClass: styles.bounceLeft,
      };
  }
}

function computeTooltipPosition(
  rect: ISpotlightRect | null,
  placement: string | undefined,
  isMobile: boolean
): React.CSSProperties {
  if (!rect) {
    if (isMobile) {
      return {
        inset: 0,
        margin: "auto",
        width: "calc(100vw - 32px)",
        height: "fit-content",
        maxHeight: dvSize("80dvh"),
        overflowY: "auto",
      };
    }
    return {
      inset: 0,
      margin: "auto",
      width: 340,
      height: "fit-content",
      maxHeight: dvSize("80dvh"),
      overflowY: "auto",
    };
  }

  const gap = TOOLTIP_GAP;

  if (isMobile) {
    // On mobile, always position below or above the target, never left/right
    const belowSpace = window.innerHeight - (rect.top + rect.height + gap);
    const aboveSpace = rect.top - gap;
    // Pick the side with more room whenever the requested side can't fit a
    // typical dialog (~260px). Prevents the buttons from being clipped off
    // the bottom of the viewport.
    const ESTIMATED_TOOLTIP_HEIGHT = 260;
    const placeAbove =
      placement === "top" ||
      (belowSpace < ESTIMATED_TOOLTIP_HEIGHT && aboveSpace > belowSpace);

    if (placeAbove) {
      return {
        bottom: window.innerHeight - rect.top + gap,
        left: 16,
        right: 16,
        maxHeight: Math.max(120, aboveSpace - 16),
        overflowY: "auto",
      };
    }
    return {
      top: rect.top + rect.height + gap,
      left: 16,
      right: 16,
      maxHeight: Math.max(120, belowSpace - 16),
      overflowY: "auto",
    };
  }

  const tooltipWidth = 340;

  switch (placement) {
    case "bottom":
      return {
        top: rect.top + rect.height + gap,
        left: Math.max(
          16,
          Math.min(
            rect.left + rect.width / 2 - tooltipWidth / 2,
            window.innerWidth - tooltipWidth - 16
          )
        ),
      };
    case "top":
      return {
        bottom: window.innerHeight - rect.top + gap,
        left: Math.max(
          16,
          Math.min(
            rect.left + rect.width / 2 - tooltipWidth / 2,
            window.innerWidth - tooltipWidth - 16
          )
        ),
      };
    case "left":
      return {
        top: Math.max(16, rect.top + rect.height / 2 - 60),
        left: Math.max(16, rect.left - gap - tooltipWidth),
      };
    case "right":
    default:
      return {
        top: Math.max(16, rect.top + rect.height / 2 - 60),
        left: Math.min(
          rect.left + rect.width + gap,
          window.innerWidth - tooltipWidth - 16
        ),
      };
  }
}
